import { Component, OnInit, OnDestroy } from "@angular/core";
import { UntypedFormGroup, UntypedFormBuilder, Validators } from '@angular/forms';
import { TranslateService } from "@ngx-translate/core";
import { SessionContextService } from "projects/packcert-web/src/app/context/session-context.service";
import { DataProvider } from 'projects/packcert-web/src/app/providers/data.provider';
import { Subject } from "rxjs";
import { takeUntil } from "rxjs/operators";
import { Message, SpinnerService, UtilsService } from 'shared-components';
import { HistoricoContextService } from "../../context/historico-context.service";
import { HistoricoService } from "../../services/historico.service";
import { Router } from "@angular/router";

@Component({
    selector: 'app-historico-totales',
    templateUrl: './historico-totales.component.html'
})
export class HistoricoTotalesComponent implements OnInit, OnDestroy {

    private readonly _DEFAULT_ITEMS_PER_PAGE = 10;

    private _filters;
    public filtersVisible = true;
    public filtersForm: UntypedFormGroup;

    public toCloseSpinner = 0;

    public historicoTotales;
    public denominaciones;
    public campanyas;
    public marcas;

    public paisId;

    public disabledDenominaciones: Subject<boolean> = new Subject();
    public disabledCampanyas: Subject<boolean> = new Subject();
    public disabledMarcas: Subject<boolean> = new Subject();

    public orderBy: string;
    public indexOrder = 6;
    public indexOrderFecha = 6;

    public destino = false;
    public fechasImpresion = false;

    public mensajes = new Array<Message>();
    public showError: boolean;
    public showWarning: boolean;

    public itemsPerPage = this._DEFAULT_ITEMS_PER_PAGE;
    public currentPage = 1;
    public firstItemList = 0;
    public lastItemList = this.itemsPerPage;
    public totalItems: number;
    public readonly visiblePages = 3;

    private readonly destroy$ = new Subject<void>();

    constructor(
        private readonly _router: Router,
        private _fb: UntypedFormBuilder,
        private readonly _translate: TranslateService,
        private readonly _sessionContext: SessionContextService,
        private readonly _spinnerService: SpinnerService,
        private readonly _historicoService: HistoricoService,
        private readonly _historicoContext: HistoricoContextService,
        private readonly _dataProvider: DataProvider,
        private readonly _utilsService: UtilsService
    ) { }

    ngOnInit() {
        this.filtersForm = this._fb.group({
            pais: ['', Validators.required],
            denominacion: [{ value: '', disabled: true }, Validators.required],
            campanya: [{ value: '', disabled: true }, Validators.required],
            marca: [{ value: '', disabled: true }],
            paisDestino: [''],
            fechaInicio: [''],
            fechaFin: [''],
            desglosePaisDestino: [false],
            fechasImpresion: [false]
        });

        this._historicoContext.historicoTotalesFilters = null;

        this.checkWarnings(this.filtersForm.get('campanya').value);

        this.filtersForm.get('fechaInicio').valueChanges.pipe(takeUntil(this.destroy$)).subscribe(() => this.validDates());
        this.filtersForm.get('fechaFin').valueChanges.pipe(takeUntil(this.destroy$)).subscribe(() => this.validDates());

        this.filtersForm.get('pais').valueChanges.pipe(takeUntil(this.destroy$)).subscribe((pais) => {
            if (pais) {
                this.paisId = pais.id;
                this.getDenominacionesByPais(pais.id);
            } else {
                this.disabledDenominaciones.next(true);
                this.denominaciones = [];
                this.filtersForm.get('denominacion').patchValue('');
                this.filtersForm.get('denominacion').disable();
            }
        });

        if (this._dataProvider.paises) {
            if (this._dataProvider.paises.length === 1 && this.filtersForm.get('pais').enabled) {
                this.filtersForm.get('pais').patchValue(this._dataProvider.paises[0]);
                this.filtersForm.get('pais').disable();
            }
        }

        this.filtersForm.get('denominacion').valueChanges.pipe(takeUntil(this.destroy$)).subscribe((denominacion) => {
            if (denominacion) {
                this.getCampanyasByDenominacion(denominacion.id, this.paisId);
            } else {
                this.disabledCampanyas.next(true);
                this.campanyas = [];
                this.filtersForm.get('campanya').patchValue('');
                this.filtersForm.get('campanya').disable();
            }
        });

        this.filtersForm.get('campanya').valueChanges.pipe(takeUntil(this.destroy$)).subscribe((campanya) => {
            if (campanya) {
                this.getMarcasByCampanya(campanya.id, this.paisId);
            } else {
                this.disabledMarcas.next(true);
                this.marcas = [];
                this.filtersForm.get('marca').patchValue('');
                this.filtersForm.get('marca').disable();
            }
        });
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    public get defaultLanguage() {
        return this._translate.getDefaultLang();
    }

    public get role() {
        return this._sessionContext.sessionUser.role;
    }

    public get paises() {
        return this._dataProvider.paises;
    }

    public get destinos() {
        return this._dataProvider.destinos;
    }

    public get isSmallScreen() {
        return this._sessionContext.isSmallScreen();
    }

    public getDenominacionesByPais(paisId) {
        let filters = {
            paisId: paisId
        };
        this._spinnerService.showSpinner();
        this.toCloseSpinner += 1;
        this._historicoService.getDenominaciones(filters).toPromise().finally(() => {
            this.checkCloseSpinner();
        }).then((resp) => {
            if (resp.lista.length > 0) {
                this.denominaciones = resp.lista;
                if (this.denominaciones.length === 1) {
                    this.filtersForm.get('denominacion').patchValue(this.denominaciones[0]);
                    this.filtersForm.get('denominacion').disable();
                    this.disabledDenominaciones.next(true);
                } else {
                    this.filtersForm.get('denominacion').enable();
                    this.disabledDenominaciones.next(false);
                }
            }
        });
    }

    public getCampanyasByDenominacion(denominacionId, paisId) {
        this.campanyas = [];
        let filters = {
            denominacionId: denominacionId,
            paisId: paisId,
            volcadoHistorico: true
        };
        this._spinnerService.showSpinner();
        this.toCloseSpinner += 1;
        this._historicoService.getCampanyasMultiSelect(filters).toPromise().finally(() => {
            this.checkCloseSpinner();
        }).then((resp) => {
            if (resp.length > 0) {
                resp.forEach((element) => {
                    this.campanyas.push({ id: element, des: element.toString() });
                });
                if (this.campanyas.length === 1) {
                    this.filtersForm.get('campanya').disable();
                    this.disabledCampanyas.next(true);
                } else {
                    this.filtersForm.get('campanya').patchValue('');
                    this.filtersForm.get('campanya').enable();
                    this.disabledCampanyas.next(false);
                }
            }
        });
    }

    public getMarcasByCampanya(campanyaId, paisId) {
        let filters = {
            campanyaId: campanyaId,
            paisId: paisId
        };
        this._spinnerService.showSpinner();
        this.toCloseSpinner += 1;
        this._historicoService.getMarcas(filters).toPromise().finally(() => {
            this.checkCloseSpinner();
        }).then((resp) => {
            if (resp.lista.length > 0) {
                this.marcas = resp.lista;
                this.filtersForm.get('marca').enable();
                this.disabledMarcas.next(false);
            }
        });
    }

    private checkWarnings(pais) {
        if (pais && this.filtersForm.valid) {
            this.filtersVisible = false;
            this.showWarning = false;
            this.mensajes = [];
            this.getHistoricoTotales();
        } else {
            this.filtersVisible = true;
            this.showWarning = true;
            this.mensajes.push({ class: 'warning', message: 'global.info.filterRequired' });
        }
    }

    public pageChange(page) {
        this.currentPage = page;
        this.firstItemList = this.itemsPerPage * (page - 1);
        this.lastItemList = this.itemsPerPage * page;
    }

    public itemsPerPageChange(items) {
        this.currentPage = 1;
        this.itemsPerPage = items;
        this.firstItemList = 0;
        this.lastItemList = items;
    }

    public validDates() {
        if (this.filtersForm.get('fechaInicio').value && this.filtersForm.get('fechaFin').value) {
            if (this.checkDates(this.filtersForm.get('fechaInicio').value, this.filtersForm.get('fechaFin').value)) {
                this.clearDatesValidators();
            } else {
                this.filtersForm.get('fechaInicio').setErrors(['Invalid date']);
                this.filtersForm.get('fechaFin').setErrors(['Invalid date']);
            }
            if (this.filtersForm.get('fechaInicio').invalid || this.filtersForm.get('fechaFin').invalid) {
                this.showError = true;
                this.mensajes.push({ class: 'error', message: 'global.fecha.error.finAnteriorInicio' });
            } else {
                this.showError = false;
                this.mensajes = [];
            }
        } else {
            this.clearDatesValidators();
            this.showError = false;
            this.mensajes = [];
        }
    }

    private clearDatesValidators() {
        if (this.filtersForm.get('fechaFin').errors && this.filtersForm.get('fechaFin').errors.includes('Invalid date')) {
            this.filtersForm.get('fechaFin').clearValidators();
            this.filtersForm.get('fechaFin').updateValueAndValidity();
        }
        if (this.filtersForm.get('fechaInicio').errors && this.filtersForm.get('fechaInicio').errors.includes('Invalid date')) {
            this.filtersForm.get('fechaInicio').clearValidators();
            this.filtersForm.get('fechaInicio').updateValueAndValidity();
        }
    }

    private checkDates(inicio, fin) {
        const inicioD = new Date(inicio);
        const finD = new Date(fin);
        return inicioD <= finD;
    }

    public checkValuesForm(): boolean {
        return (
            (this.filtersForm.get('pais').value && this.filtersForm.get('pais').enabled) ||
            (this.filtersForm.get('denominacion').value && this.filtersForm.get('denominacion').enabled) ||
            this.filtersForm.get('campanya').value ||
            this.filtersForm.get('paisDestino').value ||
            this.filtersForm.get('fechaInicio').value ||
            this.filtersForm.get('fechaFin').value ||
            this.filtersForm.get('desglosePaisDestino').value ||
            this.filtersForm.get('fechasImpresion').value) ? true : false;
    }

    public resetForm() {
        this.filtersForm.get('pais').disabled ? this.filtersForm.get('pais').patchValue(this.filtersForm.get('pais').value) : this.filtersForm.controls['pais'].reset();
        this.filtersForm.controls['paisDestino'].reset();
        this.filtersForm.get('fechaInicio').reset();
        this.filtersForm.get('fechaFin').reset();
        this.filtersForm.get('desglosePaisDestino').reset();
        this.filtersForm.get('fechasImpresion').reset();

        this.checkWarnings(this.filtersForm.get('campanya').value);
    }

    public filtrar() {
        this.validDates();
        if (this.filtersForm.invalid) {
            if (Object.keys(this.filtersForm.controls).some(key => this.filtersForm.get(key).hasError('required'))) {
                this.showError = true;
                this.mensajes.push({ class: 'error', message: 'global.error.required' });
            }
        } else {
            this.applyFilters();
        }
    }

    public applyFilters() {
        this.currentPage = 1;
        this._historicoContext.historicoTotalesFilters = {
            lsPaisIds: this.filtersForm.get('pais').value ? [this.filtersForm.get('pais').value.id] : '',
            lsDenominacionIds: this.filtersForm.get('denominacion').value ? [this.filtersForm.get('denominacion').value.id] : '',
            lsAnyos: this.filtersForm.get('campanya').value ? [this.filtersForm.get('campanya').value.id] : '',
            lsPaisDestinoIds: this.filtersForm.get('paisDestino').value ? [this.filtersForm.get('paisDestino').value.id] : null,
            lsMarcaIds: this.filtersForm.get('marca').value ? [this.filtersForm.get('marca').value.id] : '',
            mostrarDesglosePaisDestino: this.filtersForm.get('desglosePaisDestino').value ?
                this.filtersForm.get('desglosePaisDestino').value : false,
            tipoArchivo: 'Pdf',
            fechaInicio: this.filtersForm.get('fechaInicio').value ?
                this._utilsService.parseGMTtoYYYYMMDD(this.filtersForm.get('fechaInicio').value) : '',
            fechaFin: this.filtersForm.get('fechaFin').value ?
                this._utilsService.parseGMTtoYYYYMMDD(this.filtersForm.get('fechaFin').value) : '',
            orderItems: null
        };

        if (this.filtersForm.get('paisDestino').value || this.filtersForm.get('desglosePaisDestino').value) {
            this.destino = true;
            this.indexOrder = 7;
        } else {
            this.destino = false;
            this.indexOrder = 6;
        }

        if (this.filtersForm.get('fechasImpresion').value) {
            this.fechasImpresion = true;
            if (this.indexOrder === 7) {
                this.indexOrderFecha = 7;
                this.indexOrder = 9;
            } else {
                this.indexOrderFecha = 6;
                this.indexOrder = 8;
            }
        } else {
            this.fechasImpresion = false;
            this.indexOrder = 6;
        }

        this.getHistoricoTotales();
    }


    public getHistoricoTotales() {
        if (this._historicoContext.historicoTotalesFilters) {
            this._filters = this._historicoContext.historicoTotalesFilters;
        } else {
            this.applyFilters();
        }
        this._spinnerService.showSpinner();
        this._historicoService.getHistoricoTotales(this._filters).toPromise().finally(() => {
            this._spinnerService.hideSpinner();
        }).then((resp) => {
            this.totalItems = resp.lsTotales.length;
            this.historicoTotales = resp;
        },
            (error) => {
                if (error.status === 420) {
                    switch (error.error.ApiErrorCode) {
                        case 3040:
                            this.showError = true;
                            this.mensajes.push({ class: 'error', message: 'global.fecha.error.masDeUnAnyo' });
                            break;
                        case 6030:
                            this.showError = true;
                            this.mensajes.push({ class: 'error', message: 'global.error.campanyaNoAccesible' });
                            break;
                        default:
                            this.showError = true;
                            this.mensajes.push({ class: 'error', message: 'global.error.noResponse' });
                            break;
                    }
                } else {
                    this.showError = true;
                    this.mensajes.push({ class: 'error', message: 'global.error.noResponse' });
                }
            });
    }

    public exportarPdfOrExcel(tipo) {
        if (this.historicoTotales) {
            if (this._historicoContext.historicoTotalesFilters) {
                var mimeType;
                this._filters = this._historicoContext.historicoTotalesFilters;
                this._filters.tamPagina = 0;
                if (tipo === 'excel') {
                    this._filters.tipoArchivo = 'Excel';
                    mimeType = 'data:application/vnd.ms-excel;base64,';
                } else {
                    this._filters.tipoArchivo = 'Pdf';
                    mimeType = 'data:application/pdf;base64,';
                }
            }
            this._spinnerService.showSpinner();
            this.toCloseSpinner += 1;
            this._historicoService.getRepHistoricoTotales(this._filters).toPromise().finally(() => {
                this.checkCloseSpinner();
            }).then((resp) => {
                const downloadLink = document.createElement('a');
                downloadLink.href = mimeType + resp.base64Archivo;
                downloadLink.download = resp.nombreArchivo;
                downloadLink.click();
            }, (error) => {
                this.showError = true;
                this.mensajes.push({ class: 'error', message: 'global.error.file' });
            });
        } else {
            this.showError = true;
            this.mensajes.push({ class: 'error', message: 'global.error.exportNoData' });
        }
    }

    private checkCloseSpinner() {
        this.toCloseSpinner -= 1;
        if (this.toCloseSpinner === 0) {
            this._spinnerService.hideSpinner();
        }
    }

    public getIcon(prop: string) {
        let iconClass = 'fa fa-sort';
        if (this._filters && this._filters.orderItems && this._filters.orderItems[0].orderProperty.indexOf(prop) != -1) {
            iconClass = this._filters.orderItems[0].orderByDescending === true ? 'fa fa-sort-down' : 'fa fa-sort-up';
        }
        return iconClass;
    }

    public toOrderBy(columnFilter, idColumn) {
        if (this.historicoTotales && this.historicoTotales.lsTotales.length > 0) {
            if (this._historicoContext.historicoTotalesFilters) {
                this._filters = this._historicoContext.historicoTotalesFilters;
            }
        }

        if (!this.orderBy || this.orderBy.includes('DESC')) {
            this.orderBy = 'ASC' + idColumn;
            this._filters.orderItems = [
                {
                    orderProperty: columnFilter,
                    orderByDescending: false
                }
            ];
        } else if (this.orderBy.includes('ASC')) {
            this.orderBy = 'DESC' + idColumn;
            this._filters.orderItems = [
                {
                    orderProperty: columnFilter,
                    orderByDescending: true
                }
            ];
        }

        this._spinnerService.showSpinner();
        this._historicoService.getHistoricoTotales(this._filters).toPromise().finally(() => {
            this._spinnerService.hideSpinner();
        }).then((resp) => {
            this.historicoTotales = resp;
        });
    }

    public getRolePermission() {
        let disabled = false;

        if (this.role === 'Administrador') {
            disabled = false;
        } else if (this.role === 'Auditor') {
            disabled = false;
        } else if (this.role === 'Supervisor') {
            disabled = false;
        }

        return disabled;
    }

    public openDocHelp() {
        this._spinnerService.showSpinner();
        this.toCloseSpinner += 1;
        this._historicoService.getDocHelp('History_Totals.pdf').toPromise().finally(() => {
            this.checkCloseSpinner();
        }).then((resp) => {
            localStorage.setItem('base64', resp.base64Archivo);
            const url = this._router.serializeUrl(this._router.createUrlTree(['viewPdf']));
            window.open(url, '_blank');
        });
    }
}
